import { useCallback, useEffect, useState } from 'react';
import { toString } from 'lodash';

import { getSessionStorageValue, setSessionStorageValue } from '../utils/session_storage';

type SessionStorageRawValue = ReturnType<typeof getSessionStorageValue>;

export function useSessionStorage(key: string): {
  value: SessionStorageRawValue;
  updateValue: (value: SessionStorageRawValue) => void;
};
// eslint-disable-next-line no-redeclare
export function useSessionStorage<T extends (rawValue: SessionStorageRawValue) => void>(
  key: string,
  typeConverter: T
): {
  value: ReturnType<T>;
  updateValue: (value: ReturnType<T>) => void;
};
/**
 * Hook that return session storage value and setter
 */
// eslint-disable-next-line no-redeclare
export function useSessionStorage<T extends (rawValue: SessionStorageRawValue) => void>(
  key: string,
  typeConverter?: T
) {
  const [value, setValue] = useState<unknown>();

  useEffect(() => {
    // Set initial value in an effect to avoid SSR mismatch.
    const rawValue = getSessionStorageValue(key);
    setValue(typeConverter ? typeConverter(rawValue) : rawValue);
  }, [key, typeConverter]);

  const updateSessionStorageValue = useCallback(
    (value: T) => {
      setSessionStorageValue(key, toString(value));
      setValue(value);
    },
    [key]
  );

  return {
    value,
    updateValue: updateSessionStorageValue,
  };
}
